import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUpload } from 'react-icons/fa';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { SupplierInvoiceRestDataProvider } from '../../../data/SupplierInvoiceRestDataProvider';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { LedgerRestDataProvider } from '../../../data/LedgerRestDataProvider';
import { BankAccountRestDataProvider } from '../../../data/BankAccountRestDataProvider';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { Supplier } from '../../../models/Supplier';
import { FiscalYear } from '../../../models/FiscalYear';
import { Ledger } from '../../../models/Ledger';
import { BankAccount } from '../../../models/BankAccount';
import SupplierInvoiceDialog from './SupplierInvoiceDialog';
import CsvImportDialog, { CsvFieldDescriptor } from '../../common/CsvImport/CsvImportDialog';
import { ToolbarButtonConfig, ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import './SupplierInvoices.scss';
import '../../../styles/GenericSiteStyles.scss';

const SupplierInvoices: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [invoices, setInvoices] = useState<SupplierInvoice[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<SupplierInvoice | null>(null);
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>('');
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  const invoiceProvider = useMemo(() => new SupplierInvoiceRestDataProvider(), []);
  const supplierProvider = useMemo(() => new SupplierRestDataProvider(), []);
  const fiscalYearProvider = useMemo(() => new FiscalYearRestDataProvider(), []);
  const ledgerProvider = useMemo(() => new LedgerRestDataProvider(), []);
  const bankAccountProvider = useMemo(() => new BankAccountRestDataProvider(), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inv, sup, fy, ldg, ba] = await Promise.all([
        invoiceProvider.getAll(),
        supplierProvider.getAll(),
        fiscalYearProvider.getAll(),
        ledgerProvider.getAll(),
        bankAccountProvider.getAll(),
      ]);
      setInvoices(inv);
      setSuppliers(sup);
      setFiscalYears(fy);
      setLedgers(ldg);
      setBankAccounts(ba);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceProvider, supplierProvider, fiscalYearProvider, ledgerProvider, bankAccountProvider]);

  useEffect(() => { loadData(); }, [loadData]);

  const hasAutoSelected = useRef(false);

  // Auto-select the most recent fiscal year once on initial load.
  useEffect(() => {
    if (fiscalYears.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      const sorted = [...fiscalYears].sort((a, b) => b.start.localeCompare(a.start));
      setSelectedFiscalYearId(sorted[0].id);
    }
  }, [fiscalYears]);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const filteredInvoices = useMemo(() => {
    return invoices
      .filter(inv => {
        if (selectedFiscalYearId && inv.fiscalYearId !== selectedFiscalYearId) return false;
        if (onlyUnpaid && inv.isPaid) return false;
        return true;
      })
      .sort((a, b) => {
        // Nulls sort to the bottom
        if (!a.invoiceDate && !b.invoiceDate) return 0;
        if (!a.invoiceDate) return 1;
        if (!b.invoiceDate) return -1;
        return b.invoiceDate.localeCompare(a.invoiceDate); // descending
      });
  }, [invoices, selectedFiscalYearId, onlyUnpaid]);

  const columns: DetailedListColumn<SupplierInvoice>[] = useMemo(() => [
    {
      key: 'invoiceDate',
      header: t('screens:supplierInvoices.columns.invoiceDate'),
      dataField: 'invoiceDate',
      type: 'date',
      width: 130,
      fixedWidth: true,
    },
    {
      key: 'invoiceNumber',
      header: t('screens:supplierInvoices.columns.invoiceNumber'),
      dataField: 'invoiceNumber',
      type: 'string',
      width: 200,
      fixedWidth: true,
    },
    {
      key: 'supplier',
      header: t('screens:supplierInvoices.columns.supplier'),
      dataField: 'supplierId',
      render: inv => {
        const s = suppliers.find(x => x.id === inv.supplierId);
        return <span>{s?.name ?? inv.supplierId}</span>;
      },
    },
    {
      key: 'paymentDueDate',
      header: t('screens:supplierInvoices.columns.dueDate'),
      dataField: 'paymentDueDate',
      type: 'date',
      width: 120,
      fixedWidth: true,
    },
    {
      key: 'totalAmountIncVat',
      header: t('screens:supplierInvoices.columns.totalIncVat'),
      dataField: 'totalAmountIncVat',
      width: 130,
      fixedWidth: true,
      render: inv => <span>€ {(inv.totalAmountIncVat ?? 0).toFixed(2)}</span>,
    },
    {
      key: 'status',
      header: t('screens:supplierInvoices.columns.status'),
      dataField: 'isBooked',
      width: 90,
      fixedWidth: true,
      render: (inv) => {
        if (inv.isPaid) return <span className="badge bg-success">{t('common:paid')}</span>;
        if (inv.isBooked) return <span className="badge bg-success">{t('common:booked')}</span>;
        const overdue = !!inv.paymentDueDate && new Date(inv.paymentDueDate) < today;
        return <span className={`badge ${overdue ? 'bg-danger' : 'bg-secondary'}`}>{t('common:open')}</span>;
      },
    },
  ], [suppliers, today, t]);

  const openEdit = (invoice: SupplierInvoice) => {
    setSelectedInvoice(invoice);
    setDialogOpen(true);
  };

  const openAdd = () => {
    setSelectedInvoice(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = async (invoice: SupplierInvoice): Promise<SupplierInvoice> => {
    try {
      if (!invoice.id) {
        const saved = { ...invoice, id: crypto.randomUUID() };
        await invoiceProvider.add(saved);
        await loadData();
        return saved;
      } else {
        await invoiceProvider.update(invoice.id, invoice);
        await loadData();
        return invoice;
      }
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  const handleDeleteFromList = async (invoice: SupplierInvoice) => {
    try {
      await invoiceProvider.delete(invoice.id);
      await loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (invoice: SupplierInvoice) => {
    try {
      await invoiceProvider.delete(invoice.id);
      closeDialog();
      await loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleBook = async (invoice: SupplierInvoice, costLedgerId: string) => {
    try {
      await invoiceProvider.book(invoice.id, costLedgerId);
      await loadData();
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  const handleBookPayment = async (invoice: SupplierInvoice, paymentDate: string, bankAccountId: string) => {
    try {
      await invoiceProvider.bookPayment(invoice.id, paymentDate, bankAccountId);
      await loadData();
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  const handleUnbook = async (invoice: SupplierInvoice) => {
    try {
      await invoiceProvider.unbook(invoice.id);
      await loadData();
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  // CSV import ---------------------------------------------------------------

  const csvFields: CsvFieldDescriptor[] = useMemo(() => [
    { key: 'invoiceNumber',      label: t('screens:supplierInvoiceDialog.fields.invoiceNumber') },
    { key: 'invoiceDate',        label: t('screens:supplierInvoiceDialog.fields.invoiceDate') },
    { key: 'paymentDueDate',     label: t('screens:supplierInvoiceDialog.fields.paymentDueDate') },
    { key: 'paymentDate',        label: t('screens:supplierInvoiceDialog.fields.paymentDate') },
    { key: 'deliveryDate',       label: t('screens:supplierInvoiceDialog.fields.deliveryDate') },
    { key: 'receiveDate',        label: t('screens:supplierInvoiceDialog.fields.receiveDate') },
    { key: 'reference',          label: t('screens:supplierInvoiceDialog.fields.reference') },
    { key: 'supplierReference',  label: t('screens:supplierInvoiceDialog.fields.supplierReference') },
    { key: 'comments',           label: t('screens:supplierInvoiceDialog.fields.comments') },
    { key: 'totalAmountExVat',   label: t('screens:supplierInvoiceDialog.fields.totalExVat') },
    { key: 'totalAmountIncVat',  label: t('screens:supplierInvoiceDialog.fields.totalIncVat') },
  ], [t]);

  const handleCsvImport = async (file: File, mapping: Record<string, string>, delimiter: string) => {
    const result = await invoiceProvider.importCsv(file, mapping, delimiter);
    await loadData();
    return result;
  };

  // ── Toolbar configs ──────────────────────────────────────────────────────────
  const toolbarFilters = useMemo<ToolbarFilterConfig[]>(() => [
    {
      key: 'fiscalYear',
      type: 'select',
      label: t('common:fiscalYear'),
      value: selectedFiscalYearId,
      onChange: setSelectedFiscalYearId,
      allLabel: t('common:allFiscalYears'),
      options: [...fiscalYears]
        .sort((a, b) => b.start.localeCompare(a.start))
        .map(fy => ({ label: fy.name, value: fy.id })),
    },
    {
      key: 'onlyUnpaid',
      type: 'checkbox',
      label: t('common:unpaidOnly'),
      value: onlyUnpaid,
      onChange: setOnlyUnpaid,
    },
  ], [fiscalYears, selectedFiscalYearId, onlyUnpaid, t]);

  const toolbarButtons = useMemo<ToolbarButtonConfig[]>(() => [
    {
      key: '__csvImport',
      icon: <FaUpload />,
      label: t('screens:csvImport.importCsv'),
      onClick: () => setCsvImportOpen(true),
      variant: 'secondary',
      role: `${UserRoles.Admin},${UserRoles.Manager}`,
    },
  ], [t]);

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="supplier-invoices-page">
        <MainScreenHeader title={t('screens:supplierInvoices.title')} />
        {error && <ErrorMessage message={error} />}
        <DetailedListComponent
          dataSource={{ data: filteredInvoices, setData: setInvoices }}
          columns={columns}
          rowKey={inv => inv.id}
          editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          onEditRequest={openEdit}
          onAddRequest={openAdd}
          onDeleteRequest={handleDeleteFromList}
          deleteRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          toolbarFilters={toolbarFilters}
          toolbarButtons={toolbarButtons}
        />
        {loading && <div className="supplier-invoices-loading">{t('common:loading')}</div>}
        <SupplierInvoiceDialog
          open={dialogOpen}
          invoice={selectedInvoice}
          suppliers={suppliers}
          fiscalYears={fiscalYears}
          ledgers={ledgers}
          bankAccounts={bankAccounts}
          onSave={handleSave}
          onDelete={handleDelete}
          onBook={handleBook}
          onBookPayment={handleBookPayment}
          onUnbook={handleUnbook}
          onClose={closeDialog}
        />
        <CsvImportDialog
          open={csvImportOpen}
          title={t('screens:csvImport.importCsvTitle')}
          entityFields={csvFields}
          onImport={handleCsvImport}
          onClose={() => setCsvImportOpen(false)}
        />
      </div>
    </RoleGuard>
  );
};

export default SupplierInvoices;
